import React, { createContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { Timestamp, where } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import { useYearContext } from './YearContext';
import { useSyncSignal } from './SheetSyncContext';
import {
    fetchCollection,
    addToCollection,
    updateInCollection,
    deleteFromCollection,
} from '../firebase/firestoreUtils';

interface YearScopedDoc {
    id?: string;
    year?: number;
    createdAt?: Timestamp;
    updatedAt?: Timestamp;
}

export interface YearScopedCollectionValue<TData extends YearScopedDoc, TItem extends { id: string }> {
    items: TItem[];
    isLoading: boolean;
    add: (data: Omit<TData, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
    update: (item: TItem) => Promise<void>;
    remove: (id: string) => Promise<void>;
}

interface YearScopedCollectionConfig<TData extends YearScopedDoc, TItem extends { id: string }> {
    /** COLLECTIONS.* key of the Firestore collection. */
    collection: string;
    /** Wraps a raw Firestore doc in its Impl class. */
    wrap: (data: TData) => TItem;
    /**
     * Strips an item down to the fields written on update (drop id/createdAt
     * and any Impl methods). `updatedAt` is stamped by the provider.
     */
    toUpdatePayload: (item: TItem) => Partial<TData>;
    /** Human label used in error logs, e.g. "Vas teams". */
    label: string;
}

/**
 * Factory for the admin year-scoped CRUD provider pattern (auth-gated fetch on
 * mount / year change via fetchCollection, refetch after every write). Shared
 * by VasTeamsContext and VasBudgetContext; MaintenanceTasksContext predates it
 * and follows the same shape.
 */
export function createYearScopedAdminCollection<TData extends YearScopedDoc, TItem extends { id: string }>(
    config: YearScopedCollectionConfig<TData, TItem>
) {
    type Value = YearScopedCollectionValue<TData, TItem>;
    const Context = createContext<Value | undefined>(undefined);

    const Provider: React.FC<{ children: ReactNode }> = ({ children }) => {
        const { selectedYear } = useYearContext();
        // Bumped whenever a Google-Sheets sync run lands, so rows edited in the
        // spreadsheet appear here without a reload.
        const syncSignal = useSyncSignal();
        const [items, setItems] = useState<TItem[]>([]);
        const [isLoading, setIsLoading] = useState(true);

        const fetchItems = useCallback(async () => {
            const data = await fetchCollection<TData>(
                config.collection,
                [where('year', '==', selectedYear)]
            );
            setItems(data.map(config.wrap));
        }, [selectedYear]);

        useEffect(() => {
            let cancelled = false;
            const load = async (isAuthed: boolean) => {
                setIsLoading(true);
                try {
                    if (!isAuthed) {
                        if (!cancelled) setItems([]);
                        return;
                    }
                    const data = await fetchCollection<TData>(
                        config.collection,
                        [where('year', '==', selectedYear)]
                    );
                    if (!cancelled) setItems(data.map(config.wrap));
                } catch (err) {
                    console.error(`Failed to fetch ${config.label}`, err);
                } finally {
                    if (!cancelled) setIsLoading(false);
                }
            };
            const unsubscribe = onAuthStateChanged(auth, user => load(!!user));
            return () => { cancelled = true; unsubscribe(); };
        }, [selectedYear, syncSignal]);

        const add = useCallback(async (data: Omit<TData, 'id' | 'createdAt' | 'updatedAt'>) => {
            const payload = {
                ...data,
                year: data.year || selectedYear,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
            };
            await addToCollection<TData>(config.collection, payload as unknown as Omit<TData, 'id'>);
            await fetchItems();
        }, [selectedYear, fetchItems]);

        const update = useCallback(async (item: TItem) => {
            const payload = { ...config.toUpdatePayload(item), updatedAt: Timestamp.now() };
            await updateInCollection<TData>(config.collection, item.id, payload);
            await fetchItems();
        }, [fetchItems]);

        const remove = useCallback(async (id: string) => {
            await deleteFromCollection(config.collection, id);
            await fetchItems();
        }, [fetchItems]);

        const value = useMemo(
            () => ({ items, isLoading, add, update, remove }),
            [items, isLoading, add, update, remove]
        );

        return <Context.Provider value={value}>{children}</Context.Provider>;
    };

    return { Context, Provider };
}
